import storage from "csi/_base/storage";
import JsonResponse from "csi/comm/JsonResponse";
import { EMPTY_URL } from "csi/constants";
import ArrayDataStore from "csi/datastore/ArrayDataStore";
import { getEnumList } from "csi/reflection/reflectionData";
import ReflectionEnum from "csi/reflection/ReflectionEnum";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import Search from "csi/xml/Search";
import { ILocaleProvider, useLocale } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import _ from "lodash";
import { FilterData, FilterParams } from "./ComplianceAnalysisMultiFilter";

const FILTER_ITEM_ATTRIBUTES = 0;
const FILTER_ITEM_C8OBJECT = 1;
const FILTER_ITEM_STATE = 1;
const FILTER_ITEM_ANALYSIS_STATUS = 2;
const FILTER_ITEM_DATA_SHEET = 3;

const TYPE_STATE: string = "Revision.State";
const TYPE_STATE_LOCALIZATION: string = "State";
const TYPE_PROD_COMP_ANALYSIS: string = "ProductComplianceAnalysis";
const TYPE_DATA_SHEET: string = "DataSheet";

const TYPE_DOMAIN_CLASS: string = "DomainClass";
const TYPE_ATTRIBUTES: string = xml.queryKey("Node Name", null, "Child:Attributes");
const TYPE_ANALYSIS_STATUS: string = "AnalysisStatus";

const PARENT_PRODUCT_TYPES = [
	"Food",
	"Formualtedmaterial",
	"Style",
	"Material",
];

const loc: ILocaleProvider = useLocale();
// create object for main data structure
function getResObject(
	id: string,
	type: string,
	res: string,
	// store to fill second select drop down
	store: ArrayDataStore<ComponentItem> = null,
	// server request query filter
	applyQueryFilter?,
	// service file function return true if conditions require to disable second select
	disableMultiSelect = () => false,
	// second filter control to use instead of multiselect
	filterControl: (onChangeSelection: () => void) => React.ReactNode = null,
	// curent selection, is nulll if no current selection
	selection: ComponentItem[] = [],
): FilterData {
	const dataFiltered: FilterData = {
		id: id,
		name: type ? loc.attributeResource(type, res) : loc.resource(res),
		type: type,
		store,
		applyQueryFilter,
		disableMultiSelect,
		filterControl,
		selection,
	};
	return dataFiltered;
}

export function getFilterData(locationType: string, locationUrl: string): FilterParams {
	const getData = (filterId: number) => filterParams.filterData[filterId];

	// Returns a function that applies a filter for the given attribute to a query
	const getQueryFilterFunc = (filterId: number, attributeId: string, filterOperation: string, path?: string) => {
		return (query: Query) => {
			const data = getData(filterId);
			const selection = data.selection;
			const len: number = selection.length;
			let q: Query = query;
			if (len > 0) {
				if (len > 1) {
					q = q.Or();
				}
				selection.forEach((item) => {
					const itemId = filterOperation == xml.OP_MATCHES ? xml.getServerMatchValue(item.id) : item.id;
					q.Attr(attributeId, filterOperation, itemId, path);
				});
			}
		};
	};

	// STATE values store
	const multiselectStoreState = new ArrayDataStore<ComponentItem>(null, "name",
		async (): Promise<ComponentItem[]> => {
			const complianceAnalysisType: string = "ProductComplianceAnalysis";
			const search: Search = new Search().byType(complianceAnalysisType);

			search.getQuery();

			const response: JsonResponse = await search.execute();
			const resultNodes = response.getResultNodes();
			const stateSet = new Set<string>();

			resultNodes.forEach((node) => {
				const state = node.ParentDataSheetRevisionState;
				if (state) {
					stateSet.add(state);
				}
			});
			return Array.from(stateSet)
				.map((state) => {
					const stateName = state.replace(/^Revision\.State:/, "");
					return {
						id: state,
						name: stateName,
					};
				})
				.sort((a, b) => {
					return a.name.localeCompare(b.name);
				});
		},
	);

	// STATE values search query
	const StateQueryFilter = getQueryFilterFunc(FILTER_ITEM_STATE, "ParentDataSheetRevisionState", xml.OP_EQUALS);

	// ANALYSIS STATUS values store
	const multiselectStoreAnalysisStatus = new ArrayDataStore<ComponentItem>(null, "name",

		async (): Promise<ComponentItem[]> => {
			const complianceAnalysisType: string = "ProductComplianceAnalysis";
			const search: Search = new Search().byType(complianceAnalysisType);
			search.getQuery();

			const response: JsonResponse = await search.execute();
			// need to remember to remove blanks
			const result = new Set<string>(response.getResultNodes().map((item) => item.AnalysisStatus));
			return Array.from(result)
				.map((item) => {
					return {
						id: item,
						name: item,
					};
				})
				.sort((a, b) => {
					return a.name.localeCompare(b.name);
				});
		},
	);

	// ANALYSIS STATUS values search query
	const AnalysisStatusQueryFilter = getQueryFilterFunc(FILTER_ITEM_ANALYSIS_STATUS, "AnalysisStatus", xml.OP_EQUALS);

	// STATE values store
	const multiselectStoreDataSheet = new ArrayDataStore<ComponentItem>(null, "name",
		async (): Promise<ComponentItem[]> => {
			const complianceAnalysisType: string = "ProductComplianceAnalysis";
			// All resulting DataSheet nodes must be a child of the context BaseProduct
			const limitPath: string = "Child:DataSheets";
			// Path from the ProductComplianceAnalysis nodes to their parent DataSheet
			const nodePath: string = "Parent:__Parent__/Parent:ParentDataSheetRevision/Child:ProductComplianceAnalyses";

			const search: Search = new Search().setLimitPath(locationUrl, limitPath);
			search.getQuery()
				.Node(xml.PROP_TYPE, xml.OP_EQUALS, complianceAnalysisType, nodePath);

			const response: JsonResponse = await search.execute();
			const result = new Set<string>(response.getResultNodes().map((item) => item.$Name));
			return Array.from(result)
				.map((item) => {
					return {
						id: item,
						name: item,
					};
				});
		},
	);

	// STATE values search query
	const DataSheetQueryFilter = getQueryFilterFunc(FILTER_ITEM_DATA_SHEET, "__Parent__", xml.OP_EQUALS, "Child:__Parent__/Child:ParentDataSheetRevision");

	// ATTRIBUTES values store
	const multiselectStoreAttributes = new ArrayDataStore<ComponentItem>(null, "name",
		async (): Promise<ComponentItem[]> => {
			return [];
		},
	);

	// ATTRIBUTES values search query
	const AttributesQueryFilter = getQueryFilterFunc(FILTER_ITEM_ATTRIBUTES, xml.ATTR_NAME, xml.OP_MATCHES);

	// ATTRIBUTES values select disable conditions
	const disableSelectAttribute = () => false;

	// values (second select) onChange
	const onChangeSelection = (selectedFilter?: number, newValue?: any) => {
		// if C8Object selection changed - reset Attribute selection;
		const attributesData = getData(FILTER_ITEM_ATTRIBUTES);
		switch (selectedFilter) {
			case FILTER_ITEM_C8OBJECT:
				attributesData.selection = [];
				// reset attribute store for new C8Object
				if (newValue?.length == 1) {
					delete attributesData.store;
					attributesData.store = multiselectStoreAttributes;
				};
				break;
		}

		saveSelection();
	};

	// if delete C8Objects chip - make sure to remove attributes selection
	const onChipDelete = (item: ComponentItem) => {
		if (item.id == TYPE_DOMAIN_CLASS) {
			getData(FILTER_ITEM_ATTRIBUTES).selection = [];
		}
		saveSelection();
	};

	// define main data structure
	const filterParams = {
		filterData: [
			getResObject(TYPE_ATTRIBUTES, null, "ComplianceAnalysisNodeNameFilter", multiselectStoreAttributes, AttributesQueryFilter, disableSelectAttribute),
			getResObject(TYPE_STATE_LOCALIZATION, TYPE_PROD_COMP_ANALYSIS, TYPE_STATE_LOCALIZATION, multiselectStoreState, StateQueryFilter),
			getResObject(TYPE_ANALYSIS_STATUS, TYPE_PROD_COMP_ANALYSIS, TYPE_ANALYSIS_STATUS, multiselectStoreAnalysisStatus, AnalysisStatusQueryFilter),
			// Only include the DataSheets filter in the aray when the context node is a valid Parent Product
			...(
				_.includes(PARENT_PRODUCT_TYPES, locationType) ?
					[getResObject(TYPE_DATA_SHEET, TYPE_PROD_COMP_ANALYSIS, TYPE_DATA_SHEET, multiselectStoreDataSheet, DataSheetQueryFilter)] :
					[]
			),
		],
		callbackOnChangeSelection: onChangeSelection,
		callbackOnChipDelete: onChipDelete,
		chipItems: [],
		selectedFilterItem: FILTER_ITEM_C8OBJECT,
	};

	// load selection from local storage
	// Change Log "Slim View" and "Detailed View" share the same fitler save, so filter change made in one view appear in other view
	const loadSelection = () => {
		const defSelection = storage.get("ComplianceAnalysis|_CS_Site|" + locationType)?.query || [];
		filterParams.filterData.forEach((filterItem, idx) => {
			const sel = defSelection[filterItem.id] || [];
			if (sel.length) {
				let itemName;
				sel.forEach((selItem) => {
					switch (idx) {
						case FILTER_ITEM_ATTRIBUTES:
							// check for broken save: not possible to have selected attributes without single c8 class selection
							if (defSelection[TYPE_DOMAIN_CLASS] || defSelection[TYPE_DOMAIN_CLASS] == 1) {
								// to localize C8 object attribute we need to use object type
								itemName = loc.attributeResource(defSelection[TYPE_DOMAIN_CLASS][0], selItem);
							}
							break;
						case FILTER_ITEM_STATE:
							itemName = loc.enumResource(selItem);
							break;
						default:
							itemName = loc.resource(selItem);
					}
					if (selItem && itemName) {
						filterItem.selection.push({
							id: selItem,
							name: itemName,
						});
					}
				});
			}
		});
	};

	// save selection to local storage
	// Compliance Analysis "Slim View" and "Extended View" share the same filter save, so filter change made in one view appear in other view
	const saveSelection = () => {
		const storageKey: string = "ComplianceAnalysis|_CS_Site|" + locationType;
		const saveData = storage.get(storageKey) || {};

		// if no save data yet - simulate Default CV for compatibility with Cahnge Log Detailed View
		saveData.CV ||= EMPTY_URL;

		const defSelection = {};
		filterParams.filterData.forEach((filterItem) => {
			const selection = filterItem.selection;
			if (selection.length) {
				defSelection[filterItem.id] = [];

				selection.forEach((selectionItem) => {
					defSelection[filterItem.id].push(selectionItem.id);
				});
			};
		});
		saveData.query = saveData.queryForServer = defSelection;
		storage.set(storageKey, saveData);
	};

	loadSelection();

	return filterParams;
}

export default getFilterData;